import React, { ChangeEvent, FC, Fragment, useState, useEffect } from 'react';
import { Box, Grid, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    ArchivedFilterStatus,
    OrderFilter,
    SampleType,
    ProductionStatus,
    OrderOnDateFilter,
    OrderListType,
} from 'models/order';
import { permissions } from 'utils/permission';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { Steps } from 'models/workflow-step';
import DateFromToFilter from 'components/shared/date-from-to-filter/date-from-to-filter.component';
import OrderReviewSelectFilter from 'components/order-review/order-review-filter/order-review-select-filter/order-review-select-filter.component';
import { DateRange } from '@mui/lab';
import MultiSelectFilter from 'components/shared/multi-select-filter/multi-select-filter.component';
import { OrderStatusMultiSelect } from 'models/invoice';
import CustomerDropDown from 'components/shared/customer-dropdown/customer-dropdown.component';
import { ClientType, LabClient } from 'models/client-directory';
import { ClinicOrderFilter } from 'components/shared/order-filters/clinic-order-filter.components';
import { SampleDropdown } from 'components/shared/order-filters/sample-dropdown.component';
import { OrderTagDropdown } from 'components/shared/order-filters/order-tag-dropdown.component';
import { ArchivedStatusDropdown } from 'components/shared/order-filters/archived-status-dropdown.component';
import { ProductionStatusDropdown } from 'components/shared/order-filters/production-status-dropdown.component';
import { StepDropdown } from 'components/shared/order-filters/step-dropdown.component';
import { TechnicianDropdown } from 'components/shared/order-filters/technician-dropdown.component';

type OrdersPageActionsProps = {
    onFilterChange: (filters: OrderFilter) => void;
    initFilters: OrderFilter;
    hideStatusFilter?: boolean;
    hideClinicFilter?: boolean;
    hideProductionFilter?: boolean;
    hideArchiveFilter?: boolean;
    hideStepFilter?: boolean;
    hideDateFilter?: boolean;
    hideOrderTagFilter?: boolean;
    hideSupplierFilter?: boolean;
    showTechnicianFilter?: boolean;
    filtersLocalStorageKey?: string;
};

const OrdersPageActions: FC<OrdersPageActionsProps> = ({
    onFilterChange,
    initFilters,
    hideStatusFilter,
    hideClinicFilter,
    hideProductionFilter,
    hideArchiveFilter,
    hideStepFilter,
    hideDateFilter,
    hideOrderTagFilter,
    hideSupplierFilter,
    showTechnicianFilter,
    filtersLocalStorageKey = 'order-list-filters',
}: OrdersPageActionsProps) => {
    const currentUser = currentUserVar();
    const currentOrganization = currentOrgVar();
    const { t: translate } = useTranslation(['orders', 'common', 'invoices']);
    const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout>>();
    const [search, setSearch] = useState('');
    const steps: Steps[] = currentOrganization.workFlow?.steps?.map((step: Steps, index: number) => {
        return { ...step, index: index + 1 };
    });
    const [filters, setFilters] = useState({
        search: '',
        orderStatus: [] as string[],
        sample: 'ALL' as SampleType,
        archived: ArchivedFilterStatus.ALL,
        production: ProductionStatus.ALL,
        stepId: '',
        dateFilterType: '',
        dateFromTo: [null, null] as DateRange<Date>,
        orderListType: OrderListType.MY_ORDERS,
        clinicId: '' as string | null,
        orderTagIds: [] as string[],
        supplierId: '' as string | null,
        technicianId: '' as string | null,
    });

    const emitChange = (
        search: string,
        orderStatus: string[],
        sample: SampleType,
        archivedStatus: ArchivedFilterStatus,
        production: ProductionStatus,
        stepId: string,
        dateFilterType: string,
        dateFromTo: DateRange<Date>,
        orderListType: OrderListType,
        clinicId?: string | null,
        orderTagIds?: string[],
        supplierId?: string | null,
        technicianId?: string | null,
    ) => {
        const filters = {
            search,
            clinicId: clinicId || null,
            orderStatus: orderStatus,
            sample: sample !== SampleType.ALL ? sample : undefined,
            archived: archivedStatus,
            production: production !== ProductionStatus.ALL ? production : undefined,
            stepId: stepId !== 'ALL' ? stepId : undefined,
            dateFilterType: dateFilterType !== OrderOnDateFilter.NO_DATE_FILTER ? dateFilterType : undefined,
            dateFromTo: dateFilterType !== OrderOnDateFilter.NO_DATE_FILTER ? dateFromTo : undefined,
            orderListType,
            orderTagIds: orderTagIds?.length ? orderTagIds : undefined,
            supplierId: supplierId || null,
            technicianId: technicianId || null,
        };
        onFilterChange(filters);
        localStorage.setItem(filtersLocalStorageKey, JSON.stringify(filters));
    };

    const handleFilterChange = <K extends keyof typeof filters>(updates: Record<K, (typeof filters)[K]>) => {
        const updatedFilters = { ...filters, ...updates };
        setFilters(updatedFilters);
        emitChange(
            updatedFilters.search,
            updatedFilters.orderStatus,
            updatedFilters.sample,
            updatedFilters.archived,
            updatedFilters.production,
            updatedFilters.stepId,
            updatedFilters.dateFilterType,
            updatedFilters.dateFromTo,
            updatedFilters.orderListType,
            updatedFilters.clinicId,
            updatedFilters.orderTagIds,
            updatedFilters.supplierId,
            updatedFilters.technicianId,
        );
    };

    const handleTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearch(value);
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        const timeout = setTimeout(() => {
            if (value.length === 0 || value.length >= 3) {
                handleFilterChange({ search: value });
            }
        }, 300);
        setSearchTimeout(timeout);
    };

    const handleClinicChange = (orderListType: OrderListType, clinicId: string | null) => {
        if (!clinicId) {
            handleFilterChange({ orderListType, clinicId: null });
            return;
        }
        handleFilterChange({ orderListType, clinicId });
    };

    const handleLabClinicChange = (client: LabClient | null) => {
        if (client?.customer?._id) {
            handleFilterChange({ orderListType: OrderListType.ON_CLINIC, clinicId: client.customer._id as string });
            return;
        }
        handleFilterChange({ clinicId: null, orderListType: OrderListType.ALL_ORDERS });
    };

    // The backend matches supplierId against the order's supplier lab, and the suppliers query
    // has no customer, so the filter must carry the lab id.
    const handleSupplierChange = (client: LabClient | null) => {
        if (client?.lab?._id) {
            handleFilterChange({ supplierId: client.lab._id as string });
            return;
        }
        handleFilterChange({ supplierId: null });
    };

    const handleOrderOnDateChange = (dateFilterType: string) => {
        if (dateFilterType === OrderOnDateFilter.NO_DATE_FILTER) {
            handleFilterChange({ dateFromTo: [null, null], dateFilterType: OrderOnDateFilter.NO_DATE_FILTER });
        }
        setFilters((prevFilters) => ({
            ...prevFilters,
            dateFilterType: dateFilterType,
        }));
    };

    const handleDateChange = (date: DateRange<Date>) => {
        if (date[0] !== null && date[1] !== null) {
            handleFilterChange({ dateFromTo: date, dateFilterType: filters.dateFilterType });
        }
    };

    useEffect(() => {
        if (initFilters) {
            setSearch(initFilters.search || '');
            setFilters({ ...filters, ...initFilters });
        }
    }, [initFilters]);

    return (
        <Fragment>
            <Box
                sx={{
                    marginTop: 1,
                    marginBottom: 2,
                    bgcolor: 'background.paper',
                    padding: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Grid container rowSpacing={1.5} columnSpacing={1}>
                    <Grid item md={2} sm={6} xs={12}>
                        <TextField
                            fullWidth
                            id="filter-name"
                            onChange={handleTextChange}
                            name="search"
                            label={translate('orders:ORDERS_FREE_TEXT_FILTER')}
                            placeholder={translate('orders:ORDERS_FREE_TEXT_FILTER_PLACEHOLDER')}
                            value={search}
                        />
                    </Grid>
                    {hideClinicFilter !== true ? (
                        <Fragment>
                            {permissions.isDentist(currentUser.type) ? (
                                <Grid item md={2} sm={6} xs={12}>
                                    <ClinicOrderFilter
                                        clinicId={filters.clinicId}
                                        orderListType={filters.orderListType}
                                        onClinicChange={handleClinicChange}
                                        isDentistUser={permissions.isDentistUser(currentUser.role)}
                                    />
                                </Grid>
                            ) : undefined}

                            {permissions.isLabTechnician(currentUser.type) ? (
                                <Grid item md={4} sm={6} xs={12}>
                                    <CustomerDropDown
                                        clientType={ClientType.CUSTOMER}
                                        onCustomerSelect={handleLabClinicChange}
                                        selectedCustomerId={filters.clinicId}
                                    />
                                </Grid>
                            ) : undefined}
                        </Fragment>
                    ) : undefined}
                    {hideSupplierFilter !== true && permissions.isLabTechnician(currentUser.type) ? (
                        <Grid item md={4} sm={6} xs={12}>
                            <CustomerDropDown
                                clientType={ClientType.SUPPLIER}
                                onCustomerSelect={handleSupplierChange}
                                selectedCustomerId={filters.supplierId}
                            />
                        </Grid>
                    ) : undefined}
                    {hideStatusFilter !== true ? (
                        <Grid item md={2} sm={6} xs={12}>
                            <MultiSelectFilter
                                optionSelected={filters.orderStatus || []}
                                label={translate('orders:ORDER_STATUS')}
                                options={Object.values(OrderStatusMultiSelect)}
                                onOptionChange={(orderStatus: string[]) => handleFilterChange({ orderStatus })}
                                translationNamespace="orders"
                            />
                        </Grid>
                    ) : undefined}
                    <Grid item md={2} sm={6} xs={12}>
                        <SampleDropdown
                            sample={filters.sample}
                            onSampleChange={(sample: SampleType) => handleFilterChange({ sample })}
                        />
                    </Grid>

                    {hideArchiveFilter !== true ? (
                        <Grid item md={2} sm={6} xs={12}>
                            <ArchivedStatusDropdown
                                archived={filters.archived}
                                onArchivedStatusChange={(status) => handleFilterChange({ archived: status })}
                            />
                        </Grid>
                    ) : undefined}

                    {hideDateFilter !== true && permissions.isLabTechnician(currentUser.type) ? (
                        <Fragment>
                            <Grid item md={2} sm={6} xs={12}>
                                <OrderReviewSelectFilter
                                    optionSelected={filters.dateFilterType || OrderOnDateFilter.NO_DATE_FILTER}
                                    label={translate('orders:FILTER_BY_DATE')}
                                    options={Object.values(OrderOnDateFilter)}
                                    onOptionChange={handleOrderOnDateChange}
                                    translationNamespace="orders"
                                />
                            </Grid>
                            <Grid item md={4} sm={6} xs={12}>
                                <DateFromToFilter
                                    dateFromTo={filters.dateFromTo || [null, null]}
                                    dateFilterType={filters.dateFilterType || OrderOnDateFilter.NO_DATE_FILTER}
                                    onDateChange={handleDateChange}
                                />
                            </Grid>
                        </Fragment>
                    ) : undefined}

                    {hideProductionFilter !== true ? (
                        <Grid item md={2} sm={6} xs={12}>
                            <ProductionStatusDropdown
                                production={filters.production}
                                onProductionStatusChange={(status) => handleFilterChange({ production: status })}
                            />
                        </Grid>
                    ) : undefined}

                    {hideStepFilter !== true && permissions.isLabTechnician(currentUser.type) && steps?.length ? (
                        <Grid item md={2} sm={6} xs={12}>
                            <StepDropdown
                                stepId={filters.stepId}
                                onStepChange={(stepId) => handleFilterChange({ stepId })}
                            />
                        </Grid>
                    ) : undefined}
                    {hideOrderTagFilter !== true && permissions.isLabTechnician(currentUser.type) ? (
                        <Grid item md={2} sm={6} xs={12}>
                            <OrderTagDropdown
                                selectedTagIds={filters.orderTagIds}
                                onTagChange={(orderTagIds) => handleFilterChange({ orderTagIds })}
                            />
                        </Grid>
                    ) : undefined}
                    {/* DD-3391: no external tier may read the lab's user list the dropdown asks for. */}
                    {showTechnicianFilter &&
                    permissions.isLabTechnician(currentUser.type) &&
                    !permissions.isExternalTechnician(currentUser.role) ? (
                        <Grid item md={2} sm={6} xs={12}>
                            <TechnicianDropdown
                                technicianId={filters.technicianId}
                                onTechnicianChange={(technicianId) => handleFilterChange({ technicianId })}
                                showMyJobsOption
                            />
                        </Grid>
                    ) : undefined}
                </Grid>
            </Box>
        </Fragment>
    );
};

export default OrdersPageActions;
